// Registers the service worker in production builds only.
//
// Update strategy: the worker calls skipWaiting()/clientsClaim(), so a new
// deploy activates as soon as it is fetched. When that happens the browser
// fires `controllerchange` and we reload once, so nobody is left looking at a
// stale shell. The `hadController` guard keeps the very first install from
// triggering a pointless reload on the user's first visit.
// When that reload is held back (the once-a-minute guard below) or a worker is
// left waiting, utils/appUpdate.js marks an update as ready and the admin shows
// its "New version ready" bar instead.

import { markUpdateReady, watchRegistration } from "./utils/appUpdate";

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/
    )
);

const RELOAD_KEY = "swControllerReloadAt";
const RELOAD_COOLDOWN_MS = 60000;

export function register() {
  if (process.env.NODE_ENV !== "production") return;
  // RISEMN one page site: no offline shell, and an old worker from the
  // previous RISEMN site must not keep serving a stale build. Folded away in
  // the DJ RISE build (the brand is fixed at build time).
  if (process.env.REACT_APP_BRAND === "risemn") {
    unregister();
    return;
  }
  if (!("serviceWorker" in navigator)) return;

  // PUBLIC_URL is "/" here (package.json homepage is a full URL, and CRA keeps
  // only the pathname), but resolve it properly rather than concatenating -
  // string concat would produce "//service-worker.js".
  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener("load", () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`.replace(
      "//",
      "/"
    );

    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloading = false;

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // Only a genuine update should reload; the first install also fires this.
      if (!hadController || reloading) return;

      // The in-memory flag above only survives until the reload it triggers, so
      // on its own it cannot stop a loop that spans page loads: reload, update,
      // skipWaiting, controllerchange, reload. The timestamp lives in
      // sessionStorage, so at most one controllerchange reload per minute per
      // tab, whatever the worker does.
      const now = Date.now();
      let lastReloadAt = 0;
      try {
        lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
      } catch (e) {
        // Without storage we cannot prove this is not a loop, so do not reload.
        markUpdateReady("worker");
        return;
      }
      if (now - lastReloadAt < RELOAD_COOLDOWN_MS) {
        markUpdateReady("worker");
        return;
      }
      try {
        sessionStorage.setItem(RELOAD_KEY, String(now));
      } catch (e) {
        markUpdateReady("worker");
        return;
      }

      reloading = true;
      window.location.reload();
    });

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, hadController);
    } else {
      registerValidSW(swUrl, hadController);
    }
  });
}

function registerValidSW(swUrl, hadController) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // A worker that installs (or already waits) while this page is
      // controlled is a new version: the admin shows its update bar.
      watchRegistration(registration, hadController);
      // Ask the browser to look for a new worker on every load, so a deploy is
      // picked up promptly instead of on the browser's own schedule.
      registration.update().catch(() => {});
    })
    .catch((error) => {
      console.error("Service worker registration failed:", error);
    });
}

// On localhost, make sure the served file is actually a service worker (a dev
// server may return index.html for it) and unregister if not.
function checkValidServiceWorker(swUrl, hadController) {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, hadController);
      }
    })
    .catch(() => {
      console.log("No internet connection found. App is running offline mode.");
    });
}

// Emergency escape hatch: clears caches and removes the worker.
export function unregister() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.active?.postMessage({ type: "UNREGISTER_AND_CLEAR" });
      return registration.unregister();
    })
    .catch((error) => console.error(error.message));
}
